import { useEffect, useState } from "react";
import api from "../api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, Search, Plus, Copy, Send, Mail, Sparkles, Clock, AlertTriangle, Receipt, LogIn, LogOut, Ban, XCircle, Bot, Link2, Globe, Trash2, Pencil, CheckCircle2, Route } from "lucide-react";

const CAT_LABELS = { prenotazione: "Prenotazione", prima_arrivo: "Prima dell'arrivo", checkin: "Check-in", durante_soggiorno: "Durante il soggiorno", assistenza: "Assistenza", problemi: "Problemi", checkout: "Check-out", post_soggiorno: "Post soggiorno" };
const LANG_LABELS = { it: "Italiano", en: "English", es: "Español", uk: "Українська", ru: "Русский", pl: "Polski" };
const CH_LABELS = { email: "Email", whatsapp: "WhatsApp", sms: "SMS", booking: "Booking.com", airbnb: "Airbnb", messenger: "Messenger", interno: "Interno" };
const emptyTpl = { name: "", category: "prenotazione", description: "", status: "attivo", language: "it", channel: "whatsapp", audience: "ospite", body: "", keywords: [], qr_link: false };

const DASH = [
  { key: "messages_today", label: "Messaggi oggi", icon: MessageSquare, color: "#2563eb" },
  { key: "messages_auto", label: "Automatici", icon: Bot, color: "#7c3aed" },
  { key: "messages_manual", label: "Manuali", icon: Send, color: "#0891b2" },
  { key: "missing_documents", label: "Documenti mancanti", icon: AlertTriangle, color: "#d97706" },
  { key: "city_tax_to_receive", label: "City Tax da ricevere", icon: Receipt, color: "#db2777" },
  { key: "checkins_today", label: "Check-in oggi", icon: LogIn, color: "#059669" },
  { key: "checkouts_today", label: "Check-out oggi", icon: LogOut, color: "#0d9488" },
  { key: "workflows_blocked", label: "Workflow bloccati", icon: Ban, color: "#64748b" },
  { key: "send_errors", label: "Errori di invio", icon: XCircle, color: "#dc2626" },
];

export default function CommunicationCenter() {
  const [meta, setMeta] = useState(null);
  const [dash, setDash] = useState({});
  const [jdash, setJdash] = useState({});
  const [tpls, setTpls] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [compose, setCompose] = useState(null); // {tpl, bookingId, rendered}
  const [resources, setResources] = useState([]);
  const [apts, setApts] = useState([]);
  const [editRes, setEditRes] = useState(null);
  const [ovApt, setOvApt] = useState(""); const [ovVal, setOvVal] = useState("");

  const loadRes = async () => { const r = await api.get("/comm/resources"); setResources(r.data); };
  const loadTpls = async (query = "") => {
    const r = await api.get(`/comm/templates${query ? `?q=${encodeURIComponent(query)}` : ""}`);
    setTpls(r.data.templates);
  };
  useEffect(() => {
    (async () => {
      const [m, d, b, res, ap] = await Promise.all([api.get("/comm/meta"), api.get("/comm/dashboard"), api.get("/bookings"), api.get("/comm/resources"), api.get("/apartments/lookup")]);
      setMeta(m.data); setDash(d.data); setResources(res.data); setApts(ap.data || []);
      const bk = Array.isArray(b.data) ? b.data : (b.data.items || b.data.bookings || []);
      setBookings(bk);
      await loadTpls();
      api.get(`/comm/journey/dashboard?base_url=${encodeURIComponent(window.location.origin)}`).then((r) => setJdash(r.data.kpi || {})).catch(() => {});
    })();
  }, []);

  const saveRes = async () => {
    try {
      if (editRes.id) await api.put(`/comm/resources/${editRes.id}`, editRes);
      else await api.post("/comm/resources", editRes);
      toast.success("Risorsa salvata"); setEditRes(null); await loadRes();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const delRes = async (r) => { if (!window.confirm(`Eliminare ${r.label}?`)) return; try { await api.delete(`/comm/resources/${r.id}`); toast.success("Eliminata"); await loadRes(); } catch { toast.error("Errore"); } };
  const aptName = (id) => (apts.find((a) => a.id === id) || {}).name || id;

  const saveTpl = async () => {
    try {
      if (editing.id) await api.put(`/comm/templates/${editing.id}`, editing);
      else await api.post("/comm/templates", editing);
      toast.success("Template salvato"); setEditing(null); await loadTpls(q);
    } catch { toast.error("Errore salvataggio"); }
  };

  const openCompose = async (tpl) => setCompose({ tpl, bookingId: "", rendered: null });
  const doRender = async (bookingId) => {
    const r = await api.post(`/comm/templates/${compose.tpl.id}/render?booking_id=${bookingId}&base_url=${encodeURIComponent(window.location.origin)}`);
    setCompose((c) => ({ ...c, bookingId, rendered: r.data }));
  };
  const logSent = async (channel) => {
    const r = compose.rendered;
    await api.post("/comm/messages", { booking_id: compose.bookingId, template_id: compose.tpl.id, channel, language: compose.tpl.language, body: r.resolved_body, recipient: r.recipient, source: "manual", status: "sent_manual", apartment_id: (bookings.find((b) => b.id === compose.bookingId) || {}).apartment_id });
    toast.success("Registrato nello storico"); setDash((await api.get("/comm/dashboard")).data);
  };

  return (
    <div className="space-y-8" data-testid="communication-center">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Communication Center</h1>
        <p className="mt-1 text-sm text-slate-500">Tutta la comunicazione host↔ospite lungo il ciclo della prenotazione. Complementare al QR Center (info permanenti).</p>
      </div>

      {/* Dashboard */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {DASH.map((c) => {
          const Icon = c.icon;
          return (
            <Card key={c.key} data-testid={`comm-dash-${c.key}`} className="p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500"><Icon size={15} style={{ color: c.color }} /> {c.label}</div>
              <div className="mt-1 text-2xl font-bold text-slate-900">{dash[c.key] ?? "—"}</div>
            </Card>
          );
        })}
      </div>

      {/* Customer Journey KPI (Fase 2) */}
      <div>
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><Route size={16} className="text-indigo-600" /> Customer Journey</div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {[
            { key: "da_inviare", label: "Da inviare", color: "#2563EB", icon: Send },
            { key: "pronte", label: "Pronte", color: "#059669", icon: CheckCircle2 },
            { key: "bloccate", label: "Bloccate", color: "#D97706", icon: AlertTriangle },
            { key: "invii_falliti", label: "Invii falliti", color: "#DC2626", icon: XCircle },
            { key: "checkin_incompleti", label: "Check-in incompleti", color: "#7C3AED", icon: LogIn },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <Card key={c.key} data-testid={`journey-dash-${c.key}`} className="p-4">
                <div className="flex items-center gap-2 text-xs text-slate-500"><Icon size={15} style={{ color: c.color }} /> {c.label}</div>
                <div className="mt-1 text-2xl font-bold text-slate-900">{jdash[c.key] ?? "—"}</div>
              </Card>
            );
          })}
        </div>
      </div>

      <Tabs defaultValue="templates">
        <TabsList>
          <TabsTrigger value="templates" data-testid="tab-templates">Template messaggi</TabsTrigger>
          <TabsTrigger value="resources" data-testid="tab-resources">Risorse Globali</TabsTrigger>
          <TabsTrigger value="channels" data-testid="tab-channels">Canali</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="mt-4 space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="comm-search" value={q} placeholder="Cerca: documenti, wifi, city tax, recensione..." className="pl-9"
                onChange={(e) => { setQ(e.target.value); loadTpls(e.target.value); }} />
            </div>
            <Button data-testid="new-template-btn" onClick={() => setEditing({ ...emptyTpl })}><Plus size={16} className="mr-1" /> Nuovo template</Button>
          </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {tpls.map((t) => (
              <Card key={t.id} data-testid={`comm-template-${t.id}`} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-slate-900">{t.name}</h3>
                      <Badge variant="secondary">{CAT_LABELS[t.category] || t.category}</Badge>
                      <Badge variant="outline">{LANG_LABELS[t.language] || t.language}</Badge>
                      <Badge variant="outline">{CH_LABELS[t.channel] || t.channel}</Badge>
                    </div>
                    <p className="mt-1 line-clamp-2 text-sm text-slate-500">{t.body}</p>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" data-testid={`edit-tpl-${t.id}`} onClick={() => setEditing(t)}>Modifica</Button>
                  <Button size="sm" data-testid={`compose-tpl-${t.id}`} onClick={() => openCompose(t)}><Send size={14} className="mr-1" /> Componi & invia</Button>
                </div>
              </Card>
            ))}
            {tpls.length === 0 && <p className="text-sm text-slate-400">Nessun template trovato.</p>}
          </div>
        </TabsContent>

        <TabsContent value="resources" className="mt-4 space-y-4" data-testid="resources-tab">
          <div className="flex items-center justify-between">
            <p className="max-w-2xl text-sm text-slate-500">Valori riutilizzabili nei messaggi tramite placeholder. Modificando una risorsa, <b>tutti i template si aggiornano automaticamente</b> (risolti al momento dell'invio). Ogni risorsa può avere un <b>override per appartamento</b> (fallback al valore globale).</p>
            <Button data-testid="new-resource-btn" onClick={() => setEditRes({ key: "", placeholder: "", label: "", type: "link", category: "link", value: "", overrides: {}, active: true, description: "" })}><Plus size={16} className="mr-1" /> Nuova risorsa</Button>
          </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {resources.map((r) => (
              <Card key={r.id} data-testid={`resource-${r.id}`} className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Globe size={15} className="text-indigo-500" />
                      <h3 className="font-semibold text-slate-900">{r.label}</h3>
                      <Badge variant="outline" className="font-mono text-xs">{r.placeholder}</Badge>
                      {!r.active && <Badge variant="secondary">Disattiva</Badge>}
                    </div>
                    <p className="mt-1 truncate text-sm text-indigo-600">{r.value || <span className="text-slate-400">— valore non impostato —</span>}</p>
                    {Object.keys(r.overrides || {}).length > 0 && <p className="mt-1 text-xs text-slate-400">{Object.keys(r.overrides).length} override per appartamento</p>}
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button size="sm" variant="outline" data-testid={`edit-res-${r.id}`} onClick={() => setEditRes({ ...r, overrides: { ...(r.overrides || {}) } })}><Pencil size={13} /></Button>
                    <Button size="sm" variant="ghost" className="text-rose-500" data-testid={`del-res-${r.id}`} onClick={() => delRes(r)}><Trash2 size={14} /></Button>
                  </div>
                </div>
              </Card>
            ))}
            {resources.length === 0 && <p className="text-sm text-slate-400">Nessuna risorsa. Crea la prima (es. Link Tally, IBAN, numeri utili).</p>}
          </div>
        </TabsContent>

        <TabsContent value="channels" className="mt-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {(meta?.channels || []).map((c) => (
              <Card key={c.key} className="p-4">
                <div className="font-medium text-slate-800">{CH_LABELS[c.key] || c.label}</div>
                <Badge className="mt-2" variant={c.status === "attivo" ? "default" : c.status === "assistito" ? "secondary" : "outline"}>
                  {c.status === "attivo" ? "Attivo" : c.status === "assistito" ? "Assistito" : "Pianificato"}
                </Badge>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Editor template */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Modifica template" : "Nuovo template"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input data-testid="tpl-name" placeholder="Nome" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}><SelectTrigger data-testid="tpl-category"><SelectValue /></SelectTrigger><SelectContent>{(meta?.categories || []).map((c) => <SelectItem key={c} value={c}>{CAT_LABELS[c] || c}</SelectItem>)}</SelectContent></Select>
                <Select value={editing.language} onValueChange={(v) => setEditing({ ...editing, language: v })}><SelectTrigger data-testid="tpl-language"><SelectValue /></SelectTrigger><SelectContent>{(meta?.languages || []).map((l) => <SelectItem key={l} value={l}>{LANG_LABELS[l] || l}</SelectItem>)}</SelectContent></Select>
                <Select value={editing.channel} onValueChange={(v) => setEditing({ ...editing, channel: v })}><SelectTrigger data-testid="tpl-channel"><SelectValue /></SelectTrigger><SelectContent>{(meta?.channels || []).map((c) => <SelectItem key={c.key} value={c.key}>{CH_LABELS[c.key] || c.label}</SelectItem>)}</SelectContent></Select>
                <Select value={editing.audience} onValueChange={(v) => setEditing({ ...editing, audience: v })}><SelectTrigger data-testid="tpl-audience"><SelectValue /></SelectTrigger><SelectContent>{(meta?.audiences || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select>
              </div>
              <Input placeholder="Descrizione" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              <Textarea data-testid="tpl-body" rows={5} placeholder="Testo (usa [NOME OSPITE], [LINK CHECK-IN], [LINK TALLY]...)" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
              <div className="flex flex-wrap gap-1" data-testid="placeholder-chips">
                {(meta?.placeholders || []).map((p) => (
                  <button key={p.key} title={p.description || p.label} className={`rounded px-2 py-0.5 text-xs hover:bg-indigo-200 ${p.active ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-400"}`}
                    onClick={() => setEditing({ ...editing, body: editing.body + p.bracket })}>{p.bracket}</button>
                ))}
              </div>
              <Input placeholder="Parole chiave (separate da virgola)" value={(editing.keywords || []).join(", ")} onChange={(e) => setEditing({ ...editing, keywords: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button><Button data-testid="save-tpl-btn" onClick={saveTpl}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compose & send */}
      <Dialog open={!!compose} onOpenChange={(o) => !o && setCompose(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Componi & invia — {compose?.tpl?.name}</DialogTitle></DialogHeader>
          {compose && (
            <div className="space-y-3">
              <Select value={compose.bookingId} onValueChange={doRender}>
                <SelectTrigger data-testid="compose-booking"><SelectValue placeholder="Seleziona prenotazione" /></SelectTrigger>
                <SelectContent>{bookings.slice(0, 100).map((b) => <SelectItem key={b.id} value={b.id}>{b.guest_name} · {(b.check_in || "").slice(0, 10)}</SelectItem>)}</SelectContent>
              </Select>
              {compose.rendered && (
                <>
                  <Textarea data-testid="compose-body" rows={5} value={compose.rendered.resolved_body} onChange={(e) => setCompose({ ...compose, rendered: { ...compose.rendered, resolved_body: e.target.value } })} />
                  {compose.rendered.unresolved?.length > 0 ? (
                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm" data-testid="unresolved-warning">
                      <p className="flex items-center gap-1.5 font-medium text-rose-700"><AlertTriangle size={15} /> Placeholder non risolti — invio automatico bloccato</p>
                      <ul className="mt-1 list-inside list-disc text-xs text-rose-600">
                        {compose.rendered.unresolved.map((u, i) => <li key={i}><b className="font-mono">{u.placeholder}</b> — {u.reason}</li>)}
                      </ul>
                    </div>
                  ) : (
                    <p className="flex items-center gap-1.5 text-sm text-emerald-600" data-testid="resolved-ok"><CheckCircle2 size={15} /> Tutti i placeholder risolti</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" data-testid="copy-btn" onClick={() => { navigator.clipboard.writeText(compose.rendered.resolved_body); toast.success("Testo copiato"); logSent(compose.tpl.channel); }}><Copy size={14} className="mr-1" /> Copia</Button>
                    {compose.rendered.whatsapp_link && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-btn" disabled={!compose.rendered.can_auto_send} onClick={() => { window.open(compose.rendered.whatsapp_link, "_blank"); logSent("whatsapp"); }}><Send size={14} className="mr-1" /> WhatsApp</Button>}
                    {compose.rendered.mailto_link && <Button size="sm" variant="outline" data-testid="email-btn" disabled={!compose.rendered.can_auto_send} onClick={() => { window.location.href = compose.rendered.mailto_link; logSent("email"); }}><Mail size={14} className="mr-1" /> Email</Button>}
                  </div>
                  <p className="text-xs text-slate-400">Destinatario: {compose.rendered.recipient?.name} · {compose.rendered.recipient?.phone || compose.rendered.recipient?.email || "—"}</p>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Editor risorsa globale */}
      <Dialog open={!!editRes} onOpenChange={(o) => !o && setEditRes(null)}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="resource-dialog">
          <DialogHeader><DialogTitle>{editRes?.id ? "Modifica risorsa" : "Nuova risorsa globale"}</DialogTitle></DialogHeader>
          {editRes && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-500">Etichetta</label><Input data-testid="res-label" value={editRes.label} onChange={(e) => setEditRes({ ...editRes, label: e.target.value })} /></div>
                <div><label className="text-xs text-slate-500">Chiave (slug)</label><Input data-testid="res-key" value={editRes.key} disabled={!!editRes.id} onChange={(e) => { const k = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"); setEditRes({ ...editRes, key: k, placeholder: editRes.placeholder || `[${k.toUpperCase()}]` }); }} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-xs text-slate-500">Placeholder</label><Input data-testid="res-placeholder" value={editRes.placeholder} onChange={(e) => setEditRes({ ...editRes, placeholder: e.target.value })} /></div>
                <div><label className="text-xs text-slate-500">Tipo</label>
                  <Select value={editRes.type} onValueChange={(v) => setEditRes({ ...editRes, type: v })}><SelectTrigger data-testid="res-type"><SelectValue /></SelectTrigger><SelectContent>{(meta?.resource_types || []).map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><label className="text-xs text-slate-500">Categoria</label>
                  <Select value={editRes.category} onValueChange={(v) => setEditRes({ ...editRes, category: v })}><SelectTrigger data-testid="res-category"><SelectValue /></SelectTrigger><SelectContent>{(meta?.resource_categories || []).map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <div><label className="text-xs text-slate-500">Valore globale</label><Input data-testid="res-value" value={editRes.value} onChange={(e) => setEditRes({ ...editRes, value: e.target.value })} placeholder="https://…" /></div>
              <div>
                <label className="text-xs font-semibold uppercase text-slate-500">Override per appartamento</label>
                <div className="mt-1 space-y-1">
                  {Object.entries(editRes.overrides || {}).map(([aid, val]) => (
                    <div key={aid} className="flex items-center gap-2" data-testid={`override-${aid}`}>
                      <span className="w-40 truncate text-sm text-slate-600">{aptName(aid)}</span>
                      <Input className="h-8 flex-1 text-sm" value={val} onChange={(e) => setEditRes({ ...editRes, overrides: { ...editRes.overrides, [aid]: e.target.value } })} />
                      <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => { const o = { ...editRes.overrides }; delete o[aid]; setEditRes({ ...editRes, overrides: o }); }}><Trash2 size={13} /></Button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <Select value={ovApt} onValueChange={setOvApt}>
                      <SelectTrigger className="h-8 w-40 text-sm" data-testid="override-apt-select"><SelectValue placeholder="Appartamento" /></SelectTrigger>
                      <SelectContent>{apts.filter((a) => !(editRes.overrides || {})[a.id]).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="h-8 flex-1 text-sm" placeholder="Valore specifico" value={ovVal} onChange={(e) => setOvVal(e.target.value)} data-testid="override-value" />
                    <Button size="sm" variant="outline" data-testid="add-override-btn" disabled={!ovApt || !ovVal} onClick={() => { setEditRes({ ...editRes, overrides: { ...editRes.overrides, [ovApt]: ovVal } }); setOvApt(""); setOvVal(""); }}>+ Override</Button>
                  </div>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editRes.active} onChange={(e) => setEditRes({ ...editRes, active: e.target.checked })} data-testid="res-active" /> Attiva</label>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditRes(null)}>Annulla</Button><Button data-testid="save-res-btn" onClick={saveRes} disabled={!editRes?.key || !editRes?.label}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
